import { Link } from "@tanstack/react-router";

import { Card, CardHead } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchPageDetail, TeBaseline } from "@/types";
import { compactTime } from "@/utils/format";
import { DELTA_EPSILON, formatDelta, formatScore } from "@/utils/rubric";
import { finishedCount, runStats } from "./batchCells";
import { costTitle, formatCost, sumCosts } from "./cost";
import { specKey, teLetters, teName } from "./teSpec";

function duration(from: string, to: string | null): string {
  if (!to) return "—";
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

/**
 * Every run on the page, newest first (fe/TEST_CASES_BATCH.md §5 · artifact
 * "실행 기록"). The Δ column compares with **TE-A — the page's first TE**,
 * not the original production run: the baseline is what this page chose to
 * compare against (§3.2). A missing average is a dash, never 0 (C5).
 */
export function RunLog({
  page,
  baseline,
}: {
  page: BatchPageDetail;
  baseline: TeBaseline | null;
}) {
  const letters = teLetters(page.runs);
  const baselineKey = [...letters.entries()].find(([, l]) => l === "TE-A")?.[0];
  const baselineRun = page.runs.find(
    (r) => r.status !== "running" && specKey(r.te_spec) === baselineKey,
  );
  const baselineAvg = baselineRun
    ? runStats(page.cells[baselineRun.batch_run_id], page.test_case_ids).avg
    : null;
  const total = sumCosts(page.runs.map((r) => r.cost));

  return (
    <Card>
      <CardHead
        title="실행 기록"
        hint={`${page.runs.length} 회${total ? ` · 총 비용 ${formatCost(total)}` : ""} · 최신이 위`}
      >
        {page.runs.length > 0 && (
          <Link
            to="/test-cases/result/pages/$pageId/summary"
            params={{ pageId: page.page_id }}
            className="text-xs text-primary hover:underline"
          >
            결과 정리 보기
          </Link>
        )}
      </CardHead>
      {page.runs.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground">
          {page.locked
            ? "아직 실행 기록이 없습니다. TE 를 하나 골라 실행하세요."
            : "TC 를 고정하고 TE 를 골라 실행하면 여기에 쌓입니다."}
        </p>
      ) : (
        <ul>
          {page.runs.map((run) => {
            const cells = page.cells[run.batch_run_id];
            const stats = runStats(cells, page.test_case_ids);
            const letter = letters.get(specKey(run.te_spec)) ?? "TE-?";
            const live = run.status === "running";
            const isBaseline = letter === "TE-A";
            const delta =
              !isBaseline && stats.avg != null && baselineAvg != null ? stats.avg - baselineAvg : null;
            return (
              <li
                key={run.batch_run_id}
                className="grid grid-cols-[52px_minmax(0,1fr)_auto_72px_52px_60px] items-center gap-3 border-b border-border px-4 py-2 text-xs last:border-b-0 md:grid-cols-[52px_130px_minmax(0,1fr)_minmax(0,220px)_72px_52px_60px]"
              >
                <span className="rounded-full border border-border px-1.5 text-center text-[10px]">{letter}</span>
                <span className="hidden text-muted-foreground tabular-nums md:block">
                  {compactTime(run.created_at)}
                </span>
                <span className="min-w-0 truncate" title={run.started_by_email ?? undefined}>
                  {teName(run.te_spec, baseline)} ·{" "}
                  {live ? "실행 중" : duration(run.created_at, run.completed_at)}
                  {run.status === "failed" && <span className="ml-1 text-destructive">· 실패</span>}
                </span>
                <Distribution stats={stats} finished={finishedCount(cells)} live={live} />
                <span
                  className="text-right tabular-nums text-muted-foreground"
                  title={costTitle(run.cost)}
                  aria-label={run.cost ? `비용 ${formatCost(run.cost)}` : undefined}
                >
                  {formatCost(run.cost)}
                </span>
                <span className="text-right font-medium tabular-nums">
                  {stats.avg == null ? "—" : formatScore(stats.avg)}
                </span>
                <span
                  className={cn(
                    "text-right tabular-nums",
                    delta == null || Math.abs(delta) <= DELTA_EPSILON
                      ? "text-muted-foreground"
                      : delta > 0
                        ? "text-primary"
                        : "text-destructive",
                  )}
                >
                  {isBaseline ? "기준" : delta == null ? "—" : formatDelta(delta)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Distribution({
  stats,
  finished,
  live,
}: {
  stats: ReturnType<typeof runStats>;
  finished: number;
  live: boolean;
}) {
  const total = stats.total || 1;
  const parts: [string, number, string][] = [
    ["채점", stats.scored, "bg-primary"],
    ["평가 불가·채점 대기", stats.unscored, "bg-muted-foreground/40"],
    ["실패", stats.failed, "bg-destructive"],
    ["제외", stats.skipped, "bg-border"],
    ["다른 rubric", stats.excluded, "bg-border"],
    ["common rubric", stats.common, "bg-primary/60"],
  ];
  return (
    <span className="grid gap-0.5">
      <span className="flex h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        {parts.map(([label, n, color]) =>
          n ? <i key={label} className={color} style={{ width: `${(n / total) * 100}%` }} /> : null,
        )}
      </span>
      <span className="truncate text-[10px] text-muted-foreground">
        {live
          ? `진행 ${finished} / ${stats.total}`
          : `채점 ${stats.scored} · 미채점 ${stats.unscored} · 실패 ${stats.failed}${stats.skipped ? ` · 제외 ${stats.skipped}` : ""}${stats.excluded ? ` · 다른 rubric ${stats.excluded}` : ""}${stats.common ? ` · common rubric ${stats.common} · 평균 ${stats.commonAvg != null ? stats.commonAvg.toFixed(2) : "—"}` : ""}`}
      </span>
    </span>
  );
}
